/**
 * `files_changed`: the per-turn ledger of what the agent wrote to the user's cloud storage
 * (file-ops contract, section 3). Sent on RUN_FINISHED, RUN_INTERRUPTED, RUN_CANCELLED and
 * RUN_ERROR, stored on the assistant message row; rendered in the run footer and used to refresh the
 * Files panel.
 *
 * A large ledger is shortened: the WebSocket bridge cuts it to the first entries that fit, and the
 * stored row may be compacted into per-folder entries (`aggregated`, with a `count`) or dropped. Both
 * then carry `files_changed_total` (the full count) and `files_changed_truncated: true`.
 */
import {
  cloudPathFromRel,
  cloudPathParent,
  tryParseCloudPath,
  type CloudPath,
} from '@/shared/utils/cloudPath'
import {getLogger} from '@/shared/utils/logUtils'

export const FILE_CHANGE_OPS = [
  'created',
  'updated',
  'deleted',
  'moved',
  'copied',
  'folder_created',
] as const

export type FileChangeOp = (typeof FILE_CHANGE_OPS)[number]

export interface FileChange {
  op: FileChangeOp
  /** Canonical `cloud:` path (folders end with `/`). */
  path: string
  /** Source path, for `moved` and `copied`. */
  from?: string
  isFolder: boolean
  size: number | null
  /** Tool that made the change (`write_file`, `execute`, …). */
  tool?: string
  /** For an aggregated entry: how many files in the folder `path` had this op. */
  count?: number
  /** A compacted entry standing for `count` files in the folder `path` (stored rows only). */
  aggregated?: true
}

/** The `files_changed` ledger plus the flags of a shortened list, as carried on metadata and messages. */
export interface FilesChangedFields {
  filesChanged?: FileChange[]
  /** Full number of changes; only set when the list was shortened. */
  filesChangedTotal?: number
  /** The list (possibly absent) is shorter than what the turn changed. */
  filesChangedTruncated?: true
}

function isFileChangeOp(v: unknown): v is FileChangeOp {
  return typeof v === 'string' && (FILE_CHANGE_OPS as readonly string[]).includes(v)
}

function unwrapJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw) as unknown
  } catch {
    return undefined
  }
}

function dropEntry(reason: string, raw: unknown): null {
  getLogger().warn(`[files_changed] dropping entry: ${reason}`, raw)
  return null
}

/**
 * The canonical path with its trailing `/` set by `isFolder`, so links (file vs folder) and the
 * Files panel refresh agree when `is_folder` and the path disagree. The root is always a folder.
 */
function withFolderness(path: CloudPath, isFolder: boolean): CloudPath {
  if (path.isRoot || path.isFolder === isFolder) return path
  const rel = isFolder ? path.rel : path.rel.replace(/\/+$/, '')
  return tryParseCloudPath(cloudPathFromRel(rel, {isFolder})) ?? path
}

function isPositiveInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0
}

function normalizeEntry(raw: unknown): FileChange | null {
  if (!raw || typeof raw !== 'object') return dropEntry('not an object', raw)
  const o = raw as Record<string, unknown>
  if (!isFileChangeOp(o.op)) return dropEntry('unknown op', raw)
  const parsed = typeof o.path === 'string' ? tryParseCloudPath(o.path) : null
  if (!parsed) return dropEntry('path is not a valid cloud: path', raw)
  const aggregated = o.aggregated === true
  if (aggregated && !isPositiveInt(o.count)) {
    return dropEntry('aggregated entry without a valid count', raw)
  }
  const isFolderRaw = o.is_folder ?? o.isFolder
  // An aggregated entry always names the folder its files are in.
  const isFolder =
    aggregated ||
    parsed.isRoot ||
    (typeof isFolderRaw === 'boolean' ? isFolderRaw : parsed.isFolder)
  const path = withFolderness(parsed, isFolder)
  let from: CloudPath | null = null
  if (typeof o.from === 'string') {
    const parsedFrom = tryParseCloudPath(o.from)
    if (parsedFrom) from = withFolderness(parsedFrom, isFolder)
    else getLogger().warn('[files_changed] ignoring a from that is not a valid cloud: path', raw)
  }
  const size = typeof o.size === 'number' && Number.isFinite(o.size) && o.size >= 0 ? o.size : null
  const tool = typeof o.tool === 'string' && o.tool.trim() ? o.tool.trim() : undefined
  return {
    op: o.op,
    path: path.cloud,
    ...(from ? {from: from.cloud} : {}),
    isFolder,
    size,
    ...(tool ? {tool} : {}),
    ...(aggregated ? {count: o.count as number, aggregated: true as const} : {}),
  }
}

/**
 * Normalize a `files_changed` value from the wire or a persisted row (a list, or its JSON string).
 * Entries with an unknown op or a path that is not a valid `cloud:` path, and aggregated entries
 * without a positive integer `count`, are dropped (and logged);
 * a folder's path always ends in `/` and a file's never does, whatever the wire path had.
 * Returns `undefined` when nothing is left.
 */
export function normalizeFilesChanged(raw: unknown): FileChange[] | undefined {
  const value = unwrapJson(raw)
  if (!Array.isArray(value)) return undefined
  const out = value.map(normalizeEntry).filter((c): c is FileChange => c !== null)
  return out.length ? out : undefined
}

function normalizeTotal(raw: unknown): number | undefined {
  const n = typeof raw === 'string' && raw.trim() ? Number(raw) : raw
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : undefined
}

/** The raw keys of a wire frame or persisted row (either case). */
export interface FilesChangedSource {
  filesChanged?: unknown
  files_changed?: unknown
  filesChangedTotal?: unknown
  files_changed_total?: unknown
  filesChangedTruncated?: unknown
  files_changed_truncated?: unknown
}

/**
 * Reads `files_changed`, `files_changed_total` and `files_changed_truncated` (or their camelCase
 * forms) from a wire frame or a persisted row. The total and flag are kept only for a shortened
 * list, and also when the list itself was dropped.
 */
export function filesChangedFields(source: FilesChangedSource): FilesChangedFields {
  const filesChanged = normalizeFilesChanged(source.filesChanged ?? source.files_changed)
  const truncatedRaw = source.filesChangedTruncated ?? source.files_changed_truncated
  const truncated = truncatedRaw === true || truncatedRaw === 'true'
  const total = truncated
    ? normalizeTotal(source.filesChangedTotal ?? source.files_changed_total)
    : undefined
  return {
    ...(filesChanged ? {filesChanged} : {}),
    ...(total !== undefined ? {filesChangedTotal: total} : {}),
    ...(truncated ? {filesChangedTruncated: true as const} : {}),
  }
}

/** The set fields of `source` (a non-empty list, the total, the flag), to spread onto a message. */
export function pickFilesChangedFields(source: FilesChangedFields | undefined): FilesChangedFields {
  if (!source) return {}
  return {
    ...(source.filesChanged?.length ? {filesChanged: [...source.filesChanged]} : {}),
    ...(source.filesChangedTotal !== undefined
      ? {filesChangedTotal: source.filesChangedTotal}
      : {}),
    ...(source.filesChangedTruncated ? {filesChangedTruncated: true as const} : {}),
  }
}

/** Whether there is anything to show: entries, or a shortened list whose total is known. */
export function hasFilesChanged(source: FilesChangedFields | undefined): boolean {
  return !!(
    source?.filesChanged?.length ||
    (source?.filesChangedTruncated && (source.filesChangedTotal ?? 0) > 0)
  )
}

function folderOf(cloudPath: string, isFolder: boolean): string | null {
  const folder = isFolder ? cloudPath : cloudPathParent(cloudPath)
  return folder === null ? null : tryParseCloudPath(folder)?.rel ?? null
}

/**
 * Root-relative folders whose listing changed (the containing folder of each path and source, or
 * the folder itself for folder entries, aggregated ones included), in first-seen order without
 * duplicates.
 */
export function filesChangedFolders(changes: readonly FileChange[]): string[] {
  const folders = new Set<string>()
  for (const change of changes) {
    const target = folderOf(change.path, change.isFolder)
    if (target !== null) folders.add(target)
    if (change.from) {
      const source = folderOf(change.from, change.isFolder)
      if (source !== null) folders.add(source)
    }
  }
  return [...folders]
}

export const CHAT_FILES_CHANGED_EVENT = 'omics:chat-files-changed'

/**
 * Announce a finished turn's file changes. SocketProvider (always mounted, owns the query client)
 * refreshes the affected folders, so the Files panel updates even if the `files.changed`
 * notification never arrives.
 */
export function dispatchChatFilesChanged(changes: readonly FileChange[]): void {
  if (typeof window === 'undefined' || changes.length === 0) return
  window.dispatchEvent(
    new CustomEvent<readonly FileChange[]>(CHAT_FILES_CHANGED_EVENT, {detail: changes})
  )
}

/** Returns an unsubscribe function. */
export function subscribeChatFilesChanged(
  cb: (changes: readonly FileChange[]) => void
): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<readonly FileChange[]>).detail
    if (Array.isArray(detail) && detail.length) cb(detail)
  }
  window.addEventListener(CHAT_FILES_CHANGED_EVENT, handler)
  return () => window.removeEventListener(CHAT_FILES_CHANGED_EVENT, handler)
}
